import React, { useEffect, useState } from "react";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import baseUrl from "../../../../api/baseUrl";
import { toast } from "react-toastify";

type QueueVideo = {
  key: string;
  originalName?: string;
  mime?: string;
  size?: number;
  uploadedAt?: string;
  url?: string | null;
};

export const QueueVideos: React.FC = () => {
  const [videos, setVideos] = useState<QueueVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadName, setUploadName] = useState<string>("");

  const load = async () => {
    setLoading(true);
    try {
      setErrorText("");
      const res = await baseUrl.get("/client/setting/queueVideo/list");
      setVideos(res.data?.data || []);
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Failed to load queue videos";
      setErrorText(msg);
      toast.error(msg);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("video/")) {
        const msg = "Please select a video file";
        setErrorText(msg);
        toast.error(msg);
        return;
      }

      setErrorText("");
      setIsUploading(true);
      setUploadName(file.name);
      setUploadPct(0);

      const form = new FormData();
      form.append("file", file);

      const res = await baseUrl.post("/client/setting/queueVideo/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          const total = evt.total ?? 0;
          if (!total) {
            setUploadPct(null);
            return;
          }
          const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / total) * 100)));
          setUploadPct(pct);
        },
      });

      if (res.data?.success) {
        setVideos(res.data?.data || []);
        toast.success("Video uploaded");
      } else {
        const msg = res.data?.message || "Upload failed";
        setErrorText(msg);
        toast.error(msg);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Upload failed";
      setErrorText(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
      setUploadPct(null);
      setUploadName("");
      e.target.value = "";
    }
  };

  const handleDelete = async (key: string) => {
    setDeletingKey(key);
    try {
      setErrorText("");
      const res = await baseUrl.delete("/client/setting/queueVideo/delete", {
        headers: { videokey: key },
      });
      if (res.data?.success) {
        setVideos(res.data?.data || []);
        toast.success("Video deleted");
      } else {
        const msg = res.data?.message || "Delete failed";
        setErrorText(msg);
        toast.error(msg);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Delete failed";
      setErrorText(msg);
      toast.error(msg);
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-2.5 mb-3 border-b border-slate-200">
        <h2 className="text-xs font-bold text-primary mb-0">Queue Screen Videos</h2>
        <label
          className={`flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all cursor-pointer text-xs ${
            isUploading ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          <UploadIcon sx={{ fontSize: 16 }} />
          {isUploading ? "Uploading…" : "Upload video"}
          <input type="file" accept="video/*" hidden onChange={handleUpload} />
        </label>
      </div>

      {isUploading ? (
        <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
          <div className="text-xs font-semibold text-indigo-900 truncate">
            Uploading {uploadName || "video"}
          </div>
          <div className="mt-1 h-2 w-full rounded bg-indigo-100 overflow-hidden">
            <div
              className="h-2 rounded bg-indigo-500 transition-all"
              style={{ width: `${uploadPct ?? 100}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-indigo-800/90">
            {uploadPct == null ? "Processing…" : `${uploadPct}%`}
          </div>
        </div>
      ) : null}

      {errorText ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {errorText}
        </div>
      ) : null}

      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="text-xs text-slate-500">No videos uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {videos.map((v) => (
            <div key={v.key} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-black">
                {/* Signed URL from server */}
                <video
                  src={v.url || ""}
                  controls
                  muted
                  playsInline
                  style={{ width: "100%", height: 220, objectFit: "cover" }}
                />
              </div>
              <div className="p-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">
                    {v.originalName || v.key}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{v.key}</div>
                </div>
                <button
                  className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  disabled={deletingKey === v.key || isUploading}
                  onClick={() => handleDelete(v.key)}
                  type="button"
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                  {deletingKey === v.key ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

